import { Component, ElementRef, Inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { BaseCmsComponent, CmsPage, Product, ProductFacet, ProductSort, ReduxPagination } from '@kion/data-models';
import { ProductsFacade } from '@kion/data-access/data-access-products';
import { BehaviorSubject, combineLatest, interval, Observable } from 'rxjs';
import { ActivatedRoute, ResolveEnd, Router } from '@angular/router';
import { SubSink } from 'subsink';
import { debounce, map, take } from 'rxjs/operators';
import { CorrelationObserverService } from '@kion/ngrx-helper';
import { CmsConfig } from '../../config/cms.config';
import { TranslateService } from '@ngx-translate/core';
// tslint:disable-next-line:nx-enforce-module-boundaries
import { DropDownOption } from '../../../../../../shared/ui/src/lib/shared/elements/drop-down/drop-down.component';
import { DOCUMENT } from '@angular/common';

@Component({
  selector: 'kion-cms-search-result-list',
  templateUrl: './search-result-list.component.html',
  styleUrls: ['./search-result-list.component.scss']
})
export class SearchResultListComponent implements OnInit, OnDestroy {

  @Input() component: BaseCmsComponent;
  @Input() page: CmsPage;

  filterMobileVisible: boolean;

  products$: Observable<Product[]>;

  perPage$: BehaviorSubject<number> = new BehaviorSubject<number>(20);
  page$: BehaviorSubject<number> = new BehaviorSubject<number>(1);
  sort$: BehaviorSubject<string> = new BehaviorSubject<string>("");
  filterQuery$: BehaviorSubject<string> = new BehaviorSubject<string>("");

  pagination$: Observable<ReduxPagination>;

  facets$: Observable<ProductFacet[]>;
  sorts$: Observable<DropDownOption[]>;
  activeSort$: Observable<string>;

  filterVisible: boolean;

  @ViewChild('noContent') noContent: ElementRef;

  private subSink: SubSink;

  constructor(
    private productsFacade: ProductsFacade,
    private route: ActivatedRoute,
    private observer: CorrelationObserverService,
    private cmsConfig: CmsConfig,
    private router: Router,
    private translateService: TranslateService,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.subSink = new SubSink();
  }

  ngOnInit(): void {
    this.subSink.sink = this.router.events.subscribe(
      (event) => {
        if (event instanceof ResolveEnd) {
          this.filterMobileVisible = false;
          const foundDiv  = this.document.getElementById('sidebarBackdrop');
          foundDiv?.remove();
        }
      }
    );
    this.removeAllFilter();
    this.filterVisible = false;
    this.products$ = this.productsFacade.searchResults$;
    this.pagination$ = this.productsFacade.pagination$;
    this.facets$ = this.productsFacade.facets$;
    this.sorts$ = this.productsFacade.sorts$.pipe(
      map(
        (sorts: ProductSort[]) => {
          return sorts.map(
            (sort: ProductSort) => {
              return {
                key: sort.code,
                value: this.translateService.instant('filter.sort.' + sort.code),
                selected: sort.selected
              }
            }
          )
        }
      )
    );
    this.activeSort$ = this.sorts$.pipe(
      map(
        sorts => this.translateService.instant('filter.sort.by') + ' ' +  sorts.find(sort => sort.selected)?.value
      )
    );
    this.subSink.sink = combineLatest(
      [
        this.perPage$,
        this.page$,
        this.sort$,
        this.filterQuery$
      ]
    ).pipe(debounce(() => interval(500)))
      .subscribe(
      ([ perPage, page, sort, filter ]) => {
        this.productsFacade.updatePerPage(perPage);
        this.productsFacade.updatePage(page);
        if (sort) {
          this.productsFacade.updateSort(sort);
        }
        this.subSink.sink = this.observer.subscribeToAction(
          filter !== 'all' ?  this.productsFacade.search(filter ? filter : this.route.snapshot.data.useQuery ? this.route.snapshot.queryParams.query : this.component?.query,"initial") :
            this.productsFacade.search(this.route.snapshot.data.useQuery ? this.route.snapshot.queryParams.query : this.component?.query,"initial"),
          () => {
            setTimeout(() => {
              if (this.noContent) {
                this.router.navigate(
                  [
                    this.cmsConfig.routes?.searchBaseRoute,
                    'no-result'
                  ]
                ).then(() => { });
              }
            }, 1000)
          },
          () => {
            this.router.navigate(
              [
                this.cmsConfig.routes?.searchBaseRoute,
                'no-result'
              ]
            ).then(() => { });
          }
        )
      }
    );
    this.subSink.sink = this.route.queryParams.subscribe(
      param => {
        if (param.perPage) {
          this.perPage$.next(param.perPage);
        }
        if (param.page) {
          this.page$.next(param.page);
        }
        if (param.sort) {
          this.sort$.next(param.sort);
        }
        if (param.filter) {
          this.filterQuery$.next(param.filter);
        }
      }
    );
  }
  ngOnDestroy(): void {
    this.subSink.unsubscribe();
  }

  updatePerPage(perPage: number): void {
    this.router.navigate(
      this.router.url.split('?')[0].split('/'),
      {
        queryParams: {
          perPage: perPage
        },
        queryParamsHandling: "merge"
      }
    ).then(() => { });
  }

  updatePage(page: number): void {
    this.router.navigate(
      this.router.url.split('?')[0].split('/'),
      {
        queryParams: {
          page: page
        },
        queryParamsHandling: "merge"
      }
    ).then(() => { });
  }

  addFilter(query: string): void {
    this.productsFacade.updatePage(1);
    this.subSink.sink = this.observer.subscribeToAction(
      this.productsFacade.updateQuery(query),
      () => {
        this.subSink.sink = this.productsFacade.query$.pipe(take(1)).subscribe(
          q => {
            this.router.navigate(
              this.router.url.split('?')[0].split('/'),
              {
                queryParams: {
                  filter: q.join('')
                },
                queryParamsHandling: "merge"
              }
            ).then(() => { });
          }
        );
      }
    );
  }

  removeFilter(query: string): void {
    this.productsFacade.updatePage(1);
    this.subSink.sink = this.observer.subscribeToAction(
      this.productsFacade.updateQuery(query, true),
      () => {
        this.subSink.sink = this.productsFacade.query$.pipe(take(1)).subscribe(
          q => {
            this.router.navigate(
              this.router.url.split('?')[0].split('/'),
              {
                queryParams: {
                  filter: q.join('')
                },
                queryParamsHandling: "merge"
              }
            ).then(() => { });
          }
        );
      }
    );
  }

  filterToggled() {
    this.filterVisible = !this.filterVisible;
  }

  sortSelected(sort: DropDownOption) {
    this.router.navigate(
      this.router.url.split('?')[0].split('/'),
      {
        queryParams: {
          sort: sort.key
        },
        queryParamsHandling: "merge"
      }
    ).then(() => { });
  }

  removeAllFilter() {
    console.log(this.component.query);
    this.productsFacade.updatePage(1);
    this.subSink.sink = this.observer.subscribeToAction(
      this.productsFacade.updateQuery("" , true, true),
      () => {
        this.subSink.sink = this.productsFacade.query$.pipe(take(1)).subscribe(
          q => {
            this.router.navigate(
              this.router.url.split('?')[0].split('/'),
              {
                queryParams: {
                  filter: this.component.query || this.route.snapshot.queryParams.query || 'all'
                },
                queryParamsHandling: "merge"
              }
            ).then(() => { });
          }
        );
      }
    );
  }

  toggleFilterMobile() {
    this.filterMobileVisible = !this.filterMobileVisible;
    if (this.filterMobileVisible) {
      const div = this.document.createElement('div');
      div.classList.add('modal-backdrop');
      div.id = 'sidebarBackdrop';
      this.document.body.appendChild(div);
      return
    }
    const foundDiv  = this.document.getElementById('sidebarBackdrop');
    foundDiv.remove()
  }
}
